import { useContext, useEffect, useRef, useState } from 'react'
import { Flex, Form, Modal, Typography, Upload } from 'antd'
import {
  CheckCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { postRequest } from '../../../../services/base'
import { useParams } from 'react-router-dom'
import { NavContext } from '../../../../context/global_context'
import {
  CapitalHarnessContext,
  SHOW_CAPITAL_HARNESS_UPLOAD_POPUP,
} from '../context/context'
import InputText from '../../../common/InputText'
import Button from '../../../common/Button'
import { useIntl } from 'react-intl'
import EllipsisTooltip from '../../../common/EllipsisTooltip'

const CapitalHarnessBulkUpload = (props) => {
  const { vehicle } = useParams()
  const intl = useIntl()
  const [, dispatch] = useContext(NavContext)
  const [capitalHarnessState, capitalHarnessDispatch] = useContext(CapitalHarnessContext)
  const [form] = Form.useForm()
  const [uploaded, setUploaded] = useState(false)
  const listRef = useRef(null)

  useEffect(() => {
    if (capitalHarnessState?.show_upload_popup) {
      setUploaded(false)
      form.resetFields()
    }
  }, [capitalHarnessState?.show_upload_popup, form])

  const scrollRowIntoView = (rowEl) => {
    const listEl = listRef.current
    if (!listEl || !rowEl) return
    const listRect = listEl.getBoundingClientRect()
    const rowRect = rowEl.getBoundingClientRect()
    listEl.scrollTo({
      top: listEl.scrollTop + (rowRect.top - listRect.top) - 16,
      behavior: 'smooth',
    })
  }

  const onFinishFailed = ({ errorFields }) => {
    if (!errorFields?.length) return
    const first = [...errorFields]
      .filter((field) => Array.isArray(field.name) && typeof field.name[1] === 'number')
      .sort((a, b) => a.name[1] - b.name[1])[0]
    if (!first) return
    scrollRowIntoView(
      listRef.current?.querySelector(`[data-row-index="${first.name[1]}"]`)
    )
  }

  const submitBulkCapitalHarness = async (values) => {
    dispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'capitalHarness.uploadingCaption' }),
        showLoader: true,
      },
      notification: {},
    })

    const formData = new FormData()
    formData.append('total_files', values.files.length)

    values.files.forEach((item, index) => {
      formData.append(`${index}-name`, item?.name || '')
      formData.append(`${index}-abbreviation`, item?.abbreviation || '')
      formData.append(`${index}-description`, item?.description || '')
      if (item?.capital_harness_files?.[0]) {
        formData.append(
          `${index}-capital_harness_files`,
          item.capital_harness_files[0].originFileObj || item.capital_harness_files[0]
        )
      }
    })

    let res = await postRequest(
      `/api/plugin/capital_harness/bulk/upload/${vehicle}/`,
      formData,
      false,
      {
        onUploadProgress: (progressEvent) => {
          const totalLength = progressEvent.lengthComputable
            ? progressEvent.total
            : progressEvent.target.getResponseHeader('content-length') ||
              progressEvent.target.getResponseHeader(
                'x-decompressed-content-length'
              )
          const percentageCompleted = Math.round(
            (progressEvent.loaded * 100) / totalLength
          )
          if (percentageCompleted === 100) {
            dispatch({ type: 'loader', data: {}, notification: {} })
          }
        },
      }
    )
    dispatch({ type: 'loader', data: {}, notification: {} })

    if (res?.status) {
      setUploaded(true)
    } else {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'capitalHarness.bulkUploadError' }),
        },
      })
    }
  }

  const closeModal = () => {
    capitalHarnessDispatch({ type: SHOW_CAPITAL_HARNESS_UPLOAD_POPUP, data: false })
    props.onClose()
  }

  return (
    <Modal
      title={intl.formatMessage({ id: 'capitalHarness.uploadTitle' })}
      open={capitalHarnessState?.show_upload_popup}
      centered={true}
      onCancel={closeModal}
      width="90%"
      footer={null}
      className="capital-harness-bulk-upload"
      destroyOnHidden
    >
      {uploaded ? (
        <Flex
          align="center"
          justify="center"
          vertical
          gap={12}
          style={{ padding: 40 }}
        >
          <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
          <Typography.Text strong style={{ fontSize: 20 }}>
            {intl.formatMessage({ id: 'capitalHarness.uploadSuccessTitle' })}
          </Typography.Text>
          <Typography.Text type="secondary">
            {intl.formatMessage({ id: 'capitalHarness.uploadSuccessNote' })}
          </Typography.Text>
        </Flex>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={submitBulkCapitalHarness}
          onFinishFailed={onFinishFailed}
          initialValues={{ files: [{}] }}
        >
          <div className="upload-grid">
            <div className="grid-header-item">{intl.formatMessage({ id: 'generic.name' })}</div>
            <div className="grid-header-item">{intl.formatMessage({ id: 'capitalHarness.abbreviation' })}</div>
            <div className="grid-header-item">{intl.formatMessage({ id: 'generic.description' })}</div>
            <div className="grid-header-item">
              <EllipsisTooltip className='ellipsis' value={intl.formatMessage({ id: 'capitalHarness.capitalHarnessFile' })}>
                {intl.formatMessage({ id: 'capitalHarness.capitalHarnessFile' })}
              </EllipsisTooltip>
            </div>
            <div></div>
          </div>

          <div className="bulk-upload-list" ref={listRef}>
            <Form.List name="files">
              {(fields, { add, remove }) => (
                <>
                <div className='list-container'>
                  {fields.map(({ key, name }) => (
                    <div key={key} data-row-index={name} className="upload-grid">
                      <div className="grid-item-input">
                        <InputText
                          name={[name, 'name']}
                          placeholder={intl.formatMessage({ id: 'generic.name' })}
                          isRequired={true}
                          hideLabel
                        />
                      </div>
                      <div className="grid-item-input">
                        <InputText
                          name={[name, 'abbreviation']}
                          placeholder={intl.formatMessage({ id: 'capitalHarness.abbreviation' })}
                          isRequired={true}
                          hideLabel
                        />
                      </div>
                      <div className="grid-item-input">
                        <InputText
                          name={[name, 'description']}
                          type="textarea"
                          placeholder={intl.formatMessage({ id: 'generic.description' })}
                          isRequired={true}
                          hideLabel
                        />
                      </div>
                      <div className="grid-item-file">
                        <Form.Item
                          name={[name, 'capital_harness_files']}
                          valuePropName="fileList"
                          getValueFromEvent={(e) => e?.fileList || []}
                          rules={[{ required: true }]}
                          help={false}
                          style={{ marginBottom: 0 }}
                        >
                          <Upload
                            beforeUpload={() => false}
                            maxCount={1}
                            accept="text/xml"
                          >
                            <Button icon={<UploadOutlined />}>
                              {intl.formatMessage({ id: 'capitalHarness.chooseFile' })}
                            </Button>
                          </Upload>
                        </Form.Item>
                      </div>
                      <div className="grid-item-delete">
                        <Button
                          type="link"
                          className="link-button danger"
                          icon={<DeleteOutlined />}
                          disabled={fields.length === 1}
                          onClick={() => remove(name)}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="grid-footer">
                  <Flex justify="space-between">
                    <Button
                      type="link"
                      onClick={() => {
                        const nextIndex = fields.length
                        add()
                        requestAnimationFrame(() => {
                          scrollRowIntoView(
                            listRef.current?.querySelector(
                              `[data-row-index="${nextIndex}"]`
                            )
                          )
                        })
                      }}
                      className="link-button"
                      icon={<PlusOutlined />}
                    >
                      {intl.formatMessage({ id: 'capitalHarness.addAnotherFile' })}
                    </Button>
                    <Button type="primary" htmlType="submit">
                      {intl.formatMessage({ id: 'capitalHarness.saveFiles' })}
                    </Button>
                  </Flex>
                </div>
                </>
              )}
            </Form.List>
          </div>
        </Form>
      )}
    </Modal>
  )
}

export default CapitalHarnessBulkUpload
